// @flow strict

import React from 'react';

import {
    InputSelect,
    InputNumber,
} from 'components/_ReactUI_V1';

import type { ReactSelectObject } from 'types';

import {
    SelectField,
    Label,
    SectionTitle,
    WrapperMultipleInput,
} from '../styles';
import { SectionWrapper, InputNote } from './styles';
import {
    NUMBER_INPUT_PLACEHOLDER,
    STYLE_VALUES,
} from 'utils/constants';
import { clampFixedNull } from 'utils/helpers';

const INPUT_STYLES = {
    ...STYLE_VALUES.DEFAULT_SMALL_INPUT_NUMBER_STYLES,
    marginRight: '0px',
};

const MINIMUM_DATA_POINT = 0; // cannot have a negative concentration

export const ScaleOptions = [
    'linear',
    'log',
    'sqrt'
];

export const IsothermScaleSelectionWrapper = ({
    intl,
    noPadding = false,
    noMargins = false,
    showNote = true,
    scale,
    onScaleChange,
}: {
    intl: IntlType,
    showNote: boolean,
    noPadding: boolean,
    noMargins: boolean,
    scale: {
        xAxis: {
            scale: string,
            bounds: [number, number],
        },
        yAxis: {
            scale: string,
            bounds: [number, number],
        },
    },
    onScaleChange: (axis: 'xAxis' | 'yAxis', options: {
        scale: string,
        bounds: [number, number],
    }) => void,
}) => {

    const options = ScaleOptions.map((option: string) => ({
        value: option,
        label: intl.formatMessage({
            id: `components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.${option}`,
        })
    }));
    const xScale = options.find((opt) => opt.value === scale?.xAxis.scale) || null;
    const yScale = options.find((opt) => opt.value === scale?.yAxis.scale) || null;

    const xBounds = scale?.xAxis.bounds || [null, null];
    const yBounds = scale?.yAxis.bounds || [null, null];


    const onChange = (axis: 'xAxis' | 'yAxis', boundary: 0 | 1, doBlur = false) => (event: InputEvent) => {
        const bounds = axis === 'xAxis' ? xBounds : yBounds;
        const rawValue = event.target.value;
        const value = Number(rawValue);

        let newBounds;
        const isLowerBound = boundary === 0;
        if(isLowerBound) {
            newBounds = [value || null, bounds[1]];
        } else {
            newBounds = [bounds[0], value || null];
        }

        if(doBlur && rawValue !== '') { // allow nullish values left in the box (do not clamp to 0 if left empty)
            const clamped = isLowerBound
                ? clampFixedNull(
                    value,
                    MINIMUM_DATA_POINT,
                    bounds[1]
                )
                : clampFixedNull(
                    value,
                    bounds[0] || MINIMUM_DATA_POINT,
                    null // we are setting the upper bound, no max
                );
            newBounds = isLowerBound ? [clamped, bounds[1]] : [bounds[0], clamped];
        }

        onScaleChange(axis, {
            bounds: newBounds,
        });
    }
    return (
        <SectionWrapper
            style={{
                marginBottom: '75px', // to allow for drop down
                ...(noMargins ? { margin: '0px' } : {}),
                ...(noPadding ? { padding: '0px' } : {}),
            }}
        >
            <SectionTitle>
                {intl.formatMessage({
                    id:
                        'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.Title',
                })}
            </SectionTitle>

            <SelectField>
                <Label>
                    {intl.formatMessage({
                        id:
                            'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.xAxis.Label',
                    })}
                </Label>
                <InputSelect
                    selectedOption={xScale}
                    options={options}
                    onSelect={(selected: ReactSelectObject) => onScaleChange('xAxis', {
                        scale: selected.value,
                    })}
                    placeholder={intl.formatMessage({
                        id:
                            'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.axisScaleSelectPlaceholder',
                    })}
                    controlShouldRenderValue
                />
            </SelectField>
            <SelectField bottomBorder>
                <Label>
                    {intl.formatMessage({
                        id:
                            'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.domainBounds.Label',
                    })}
                </Label>
                <WrapperMultipleInput gap="8px">
                    <InputNumber
                        onChange={onChange('xAxis', 0, false)}
                        onBlur={onChange('xAxis', 0, true)}
                        noSpinner
                        value={xBounds[0]}
                        min={MINIMUM_DATA_POINT}
                        max={xBounds[1]}
                        placeholder={NUMBER_INPUT_PLACEHOLDER}
                        textAlign="center"
                        style={INPUT_STYLES}
                    />
                    <InputNumber
                        onChange={onChange('xAxis', 1, false)}
                        onBlur={onChange('xAxis', 1, true)}
                        min={MINIMUM_DATA_POINT}
                        noSpinner
                        value={xBounds[1]}
                        placeholder={NUMBER_INPUT_PLACEHOLDER}
                        textAlign="center"
                        style={INPUT_STYLES}
                    />
                </WrapperMultipleInput>
            </SelectField>

            <SelectField>
                <Label>
                    {intl.formatMessage({
                        id:
                            'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.yAxis.Label',
                    })}
                </Label>
                <InputSelect
                    selectedOption={yScale}
                    options={options}
                    onSelect={(selected: ReactSelectObject) => onScaleChange('yAxis', {
                        scale: selected.value,
                    })}
                    placeholder={intl.formatMessage({
                        id:
                            'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.axisScaleSelectPlaceholder',
                    })}
                    controlShouldRenderValue
                />
            </SelectField>

            <SelectField>
                <Label>
                    {intl.formatMessage({
                        id:
                            'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.rangeBounds.Label',
                    })}
                </Label>
                <WrapperMultipleInput gap="8px">
                    <InputNumber
                        onChange={onChange('yAxis', 0, false)}
                        onBlur={onChange('yAxis', 0, true)}
                        noSpinner
                        value={yBounds[0]}
                        min={MINIMUM_DATA_POINT}
                        max={yBounds[1]}
                        placeholder={NUMBER_INPUT_PLACEHOLDER}
                        textAlign="center"
                        style={INPUT_STYLES}
                    />
                    <InputNumber
                        onChange={onChange('yAxis', 1, false)}
                        onBlur={onChange('yAxis', 1, true)}
                        noSpinner
                        value={yBounds[1]}
                        min={MINIMUM_DATA_POINT}
                        placeholder={NUMBER_INPUT_PLACEHOLDER}
                        textAlign="center"
                        style={INPUT_STYLES}
                    />
                </WrapperMultipleInput>
            </SelectField>

            {showNote && (
                <SelectField>
                    <InputNote>
                        {intl.formatMessage({
                            id:
                                'components.IsothermManagementSidebar.CreatePredictIsotherms.diagramDisplaySection.notPersistedWarning',
                        })}
                    </InputNote>
                </SelectField>
            )}


        </SectionWrapper>
    );
}
